import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import {
  fetchOrganisations as fetchOrganisationsAction,
  saveOrganisation as saveOrganisationAction,
} from './organisationActions';
import { AppState } from '../store/store';
import OrganisationModal, { OrganisationModalValues } from './OrganisationModal';
import ExportOrganisationModal from './ExportOrganisationModal';
import SalvPage from '../common/SalvPage';
import { SalvSearchField, SalvSwitch, SalvTable, SalvTablePaginationType, Size } from '@salvapp/ui-components';
import { createColumnHelper, PaginationState } from '@tanstack/react-table';
import { ApiOrganisation } from '../api';
import FormattedDateTime from '../common/datetime/FormattedDateTime';

const columnHelper = createColumnHelper<ApiOrganisation>();

export const Organisations = ({ page, size, total, organisations, fetchOrganisations, saveOrganisation }: Props) => {
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: page, pageSize: size });

  useEffect(() => {
    fetchOrganisations(pagination.pageIndex, pagination.pageSize, includeInactive, query);
  }, [pagination.pageIndex, pagination.pageSize, query, includeInactive, fetchOrganisations]);

  const handleChange = useCallback(
    async (values?: OrganisationModalValues) => {
      if (values) {
        await saveOrganisation({ ...values, demo: values.source === 'demo' });
      }
      await fetchOrganisations(pagination.pageIndex, pagination.pageSize, includeInactive, query);
    },
    [saveOrganisation, fetchOrganisations, pagination.pageIndex, pagination.pageSize, includeInactive, query]
  );

  const onSearch = useCallback((searchQuery: string) => {
    setQuery(searchQuery || undefined);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: '#', size: 6 }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('currency', { header: 'Currency', size: 14 }),
      columnHelper.accessor('createdTime', {
        header: 'Created',
        cell: ({ getValue }) => <FormattedDateTime date={getValue()!} showTime />,
        size: 20,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span style={{ display: 'block', textAlign: 'right' }}>Actions</span>,
        size: 20,
        cell: ({ row }) => (
          <div style={{ display: 'inline-flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            <ExportOrganisationModal organisation={row.original} />
            <OrganisationModal organisation={row.original} onChange={handleChange} />
          </div>
        ),
      }),
    ],
    [handleChange]
  );

  return (
    <SalvPage title={'Organisations'}>
      <SalvTable<ApiOrganisation>
        data={organisations}
        columns={columns}
        emptyMessage={query ? `No organisations match "${query}".` : 'No organisations yet.'}
        title={
          <SalvSearchField
            value={query ?? ''}
            onChange={onSearch}
            placeholder='Search'
            size={Size.SMALL}
            ariaLabel='Search organisations'
          />
        }
        pagination={{
          type: SalvTablePaginationType.PAGINATION,
          total,
          state: pagination,
          setPagination,
        }}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SalvSwitch
              selected={includeInactive}
              onChange={() => setIncludeInactive(!includeInactive)}
              data-testid='includeInactiveSwitch'
              label={'Show deactivated'}
            />
            <OrganisationModal onChange={handleChange} />
          </div>
        }
      />
    </SalvPage>
  );
};

export const mapStateToProps = (state: AppState) => ({
  ...state.organisation,
});

export const mapDispatchToProps = {
  fetchOrganisations: fetchOrganisationsAction,
  saveOrganisation: saveOrganisationAction,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(Organisations);
